import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  FaRegFileAlt,
  FaRegClipboard,
  FaIdCard,
  FaRegImage,
  FaFileAlt,
} from "react-icons/fa";

// Fondo por defecto
import fondoRequisitosDefault from "../assets/img/fondoRequisitos.webp";

// Imágenes de los requisitos
import fotoRequisito from "../assets/img/fotos.webp";
import certificadoRequisito from "../assets/img/certificado.webp";
import carnetRequisito from "../assets/img/carnet.webp";
import tituloRequisito from "../assets/img/titulo.webp";

// JSON con info de cada sede
import dataJson from "../assets/json/datos.json";
import { useParams } from "react-router-dom";

// Componente individual para los requisitos extranjeros (maneja el "Ver más")
const RequisitoExtranjero = ({ req, itemVariants }) => {
  const [expanded, setExpanded] = useState(false);
  const maxLength = 60; // Cantidad de caracteres antes de cortar
  const isLong = req.texto.length > maxLength;

  return (
    <motion.li variants={itemVariants} className="w-100" layout>
      <motion.div
        layout
        onClick={() => isLong && setExpanded(!expanded)}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-start",
          gap: "1rem",
          fontWeight: "600",
          fontSize: "clamp(0.95rem, 2.5vw, 1.1rem)",
          color: "#001A66",
          backgroundColor: "white",
          minHeight: "3rem",
          borderRadius: "15px",
          padding: "0.8rem 1.5rem",
          boxShadow: "0 4px 10px rgba(0,0,0,0.05)",
          cursor: isLong ? "pointer" : "default",
        }}
        whileHover={
          isLong
            ? { scale: 1.02, boxShadow: "0 6px 15px rgba(0, 0, 0, 0.15)" }
            : {}
        }
        whileTap={isLong ? { scale: 0.98 } : {}}
      >
        {req.icon && (
          <motion.span
            layout
            style={{
              color: "#009DFA",
              fontSize: "1.3rem",
              display: "flex",
              alignItems: "center",
            }}
          >
            {req.icon}
          </motion.span>
        )}
        <motion.div layout style={{ flex: 1, textAlign: "left" }}>
          <span>
            {isLong && !expanded
              ? `${req.texto.substring(0, maxLength)}...`
              : req.texto}
          </span>
          {isLong && (
            <motion.span
              layout
              style={{
                color: "#009DFA",
                fontWeight: "bold",
                marginLeft: "8px",
                whiteSpace: "nowrap",
                fontSize: "0.9rem",
              }}
            >
              {expanded ? "Ver menos" : "Ver más"}
            </motion.span>
          )}
        </motion.div>
      </motion.div>
    </motion.li>
  );
};

export default function Requisitos() {
  const { sede } = useParams();

  const requisitosNacionales = [
    {
      texto: "Fotocopia legalizada del Título de Bachiller",
      icon: <FaRegFileAlt />,
      foto: tituloRequisito,
    },
    {
      texto: "2 Fotocopias simple de CI Vigente",
      icon: <FaIdCard />,
      foto: carnetRequisito,
    },
    {
      texto: "Certificado de nacimiento original",
      icon: <FaRegClipboard />,
      foto: certificadoRequisito,
    },

    {
      texto: "3 Fotos fondo blanco 3x4",
      icon: <FaRegImage />,
      foto: fotoRequisito,
    },
  ];

  const requisitosExtranjeros = [
    {
      texto:
        "Fotocopia de diploma de bachiller legalizado por: Ministerio de Educación del país de origen; Ministerio de Exteriores del país de origen.",
      icon: <FaFileAlt />,
    },
    {
      texto:
        "Certificado de nacimiento original o fotocopia, debidamente legalizado por: el Ministerio de Exteriores y su respectiva traducción (si corresponde); Embajada de Bolivia en el país.",
      icon: <FaFileAlt />,
    },
    {
      texto: "Fotocopia del pasaporte",
      icon: <FaIdCard />,
    },
    {
      texto: "Visa de estudiante",
      icon: <FaIdCard />,
    },
    {
      texto: "3 fotos fondo blanco 3x4",
      icon: <FaRegImage />,
    },
  ];

  // LOCALSTORAGE PERSISTENCIA: Inicialización limpia y síncrona
  const [activeTab, setActiveTab] = useState(() => {
    const saved = localStorage.getItem("activeTabRequisitos");
    return saved === "nacionales" || saved === "extranjeros"
      ? saved
      : "nacionales";
  });

  const [selectedReqIndex, setSelectedReqIndex] = useState(null);
  const [imagesLoaded, setImagesLoaded] = useState(false);
  const [fondoImg, setFondoImg] = useState(fondoRequisitosDefault);

  const dataSede = dataJson[sede] || {};
  const whatsapp = dataSede.whatsapp || "";
  const mensaje =
    "Hola, deseo obtener más información sobre los requisitos de admisión.";
  const telefonoLimpio = whatsapp.replace(/\D/g, "");
  const linkWhatsapp = telefonoLimpio
    ? `https://wa.me/${telefonoLimpio}?text=${encodeURIComponent(mensaje)}`
    : "#";

  // Guardar cambios directamente en localStorage
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    localStorage.setItem("activeTabRequisitos", tab);
    setSelectedReqIndex(null);
  };

  useEffect(() => {
    const imagePromises = requisitosNacionales
      .filter((r) => r.foto)
      .map(
        (r) =>
          new Promise((resolve) => {
            const img = new Image();
            img.src = r.foto;
            img.onload = resolve;
          }),
      );
    Promise.all(imagePromises).then(() => setImagesLoaded(true));
  }, []);

  useEffect(() => {
    if (!sede) return;

    // Al cambiar la subsede de la URL, mantenemos la pestaña guardada en el localStorage
    const tabGuardada = localStorage.getItem("activeTabRequisitos");
    if (tabGuardada === "nacionales" || tabGuardada === "extranjeros") {
      setActiveTab(tabGuardada);
    } else {
      setActiveTab("nacionales");
    }

    const dataSede = dataJson[sede];
    if (dataSede?.imagenes?.fondoRequisitos) {
      try {
        const fondo = require(
          `../assets/img/${dataSede.imagenes.fondoRequisitos}`,
        );
        setFondoImg(fondo);
      } catch {
        setFondoImg(fondoRequisitosDefault);
      }
    } else {
      setFondoImg(fondoRequisitosDefault);
    }
    setSelectedReqIndex(null);
  }, [sede]);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1 },
    },
    exit: { opacity: 0, scale: 0.95, transition: { duration: 0.2 } },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 15 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { type: "spring", stiffness: 300, damping: 24 },
    },
  };

  return (
    <section
      id="requisitos"
      className="py-5"
      style={{
        background: `linear-gradient(to right, rgba(0, 40, 120, 0.75), rgba(11, 67, 163, 0.75)), url(${fondoImg}) center/cover no-repeat`,
        minHeight: "100vh",
      }}
    >
      <div style={{ display: "none" }}>
        {requisitosNacionales.map((req, i) =>
          req.foto ? <img key={i} src={req.foto} alt="" /> : null,
        )}
      </div>

      <div className="container text-center px-3 px-md-4">
        <motion.h2
          className="mb-5"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          style={{
            fontSize: "clamp(1.8rem, 5vw, 2.2rem)",
            color: "white",
            fontWeight: "bold",
          }}
        >
          Requisitos de Admisión
        </motion.h2>

        {/* CONTENEDOR DEL SWITCH CON KEY DINÁMICA POR SEDE */}
        <div
          key={`switch-container-${sede}`}
          className="mb-5 mx-auto"
          style={{
            display: "flex",
            maxWidth: "500px",
            backgroundColor: "white",
            borderRadius: "15px",
            padding: "0.4rem",
            boxShadow: "0 4px 15px rgba(0,0,0,0.2)",
            position: "relative",
          }}
        >
          {/* EL CAMBIO CLAVE PARA EL BUG DE CARGA:
              En lugar de mover un div usando porcentajes "0%" y "100%", calculamos la posición de la pastilla azul
              haciendo uso de flexbox nativo de CSS cambiando el valor de la propiedad left según corresponda. 
              Además, usamos el 'key' basado en el tab guardado para obligar al navegador a pintarlo en su sitio 
              exacto desde el primer instante de la recarga dura (F5).
          */}
          <motion.div
            key={`pill-${activeTab}`}
            layoutId="activeTabPill"
            transition={{ type: "spring", stiffness: 300, damping: 30 }}
            style={{
              position: "absolute",
              width: "calc(50% - 0.4rem)",
              height: "calc(100% - 0.8rem)",
              backgroundColor: "#009DFA",
              borderRadius: "12px",
              zIndex: 1,
              left: activeTab === "nacionales" ? "0.4rem" : "calc(50% + 0rem)",
            }}
          />

          {/* Botón Nacionales */}
          <button
            onClick={() => handleTabChange("nacionales")}
            style={{
              flex: 1,
              padding: "0.8rem",
              border: "none",
              backgroundColor: "transparent",
              color: activeTab === "nacionales" ? "white" : "#001A66",
              fontWeight: "bold",
              fontSize: "clamp(0.9rem, 2.5vw, 1.1rem)",
              cursor: "pointer",
              position: "relative",
              zIndex: 2,
              transition: "color 0.3s ease",
            }}
          >
            Nacionales
          </button>

          {/* Botón Extranjeros */}
          <button
            onClick={() => handleTabChange("extranjeros")}
            style={{
              flex: 1,
              padding: "0.8rem",
              border: "none",
              backgroundColor: "transparent",
              color: activeTab === "extranjeros" ? "white" : "#001A66",
              fontWeight: "bold",
              fontSize: "clamp(0.9rem, 2.5vw, 1.1rem)",
              cursor: "pointer",
              position: "relative",
              zIndex: 2,
              transition: "color 0.3s ease",
            }}
          >
            Extranjeros
          </button>
        </div>

        {/* CONTENIDO DESPLEGABLE */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <AnimatePresence mode="wait">
            {activeTab === "nacionales" && (
              <motion.ul
                key={`nacionales-${sede}`}
                variants={containerVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
                className="list-unstyled d-flex flex-column align-items-center gap-3 w-100"
                style={{ maxWidth: "500px" }}
              >
                {requisitosNacionales.map((req, index) => (
                  <motion.li
                    key={`nac-${index}`}
                    variants={itemVariants}
                    className="w-100"
                    layout
                  >
                    <motion.div
                      layout
                      onClick={() =>
                        setSelectedReqIndex(
                          index === selectedReqIndex ? null : index,
                        )
                      }
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "flex-start",
                        gap: "1rem",
                        fontWeight: "600",
                        fontSize: "clamp(0.95rem, 2.5vw, 1.1rem)",
                        color: "#001A66",
                        backgroundColor: "white",
                        minHeight: "3rem",
                        borderRadius: "15px",
                        cursor: "pointer",
                        padding: "0.8rem 1.5rem",
                      }}
                      whileHover={{
                        scale: 1.02,
                        boxShadow: "0 6px 15px rgba(0, 0, 0, 0.15)",
                      }}
                      whileTap={{ scale: 0.98 }}
                    >
                      {req.icon && (
                        <span style={{ color: "#009DFA", fontSize: "1.3rem" }}>
                          {req.icon}
                        </span>
                      )}
                      <span style={{ flex: 1, textAlign: "left" }}>
                        {req.texto}
                      </span>
                    </motion.div>

                    {/* FOTO DESPLEGABLE */}
                    <AnimatePresence>
                      {selectedReqIndex === index && (
                        <motion.div
                          layout
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: "auto" }}
                          exit={{ opacity: 0, height: 0 }}
                          transition={{
                            opacity: { duration: 0.2 },
                            height: { duration: 0.3 },
                          }}
                          style={{
                            overflow: "hidden",
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            gap: "1rem",
                            paddingTop: "0.8rem",
                          }}
                        >
                          {req.foto && imagesLoaded && (
                            <motion.img
                              src={req.foto}
                              alt={req.texto}
                              className="img-fluid rounded"
                              style={{
                                width: "100%",
                                maxHeight: "350px",
                                objectFit: "contain",
                              }}
                            />
                          )}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </motion.li>
                ))}
              </motion.ul>
            )}

            {activeTab === "extranjeros" && (
              <motion.ul
                key={`extranjeros-${sede}`}
                variants={containerVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
                className="list-unstyled d-flex flex-column align-items-center gap-3 w-100"
                style={{ maxWidth: "500px" }}
              >
                {requisitosExtranjeros.map((req, index) => (
                  <RequisitoExtranjero
                    key={`ext-${index}`}
                    req={req}
                    itemVariants={itemVariants}
                  />
                ))}
              </motion.ul>
            )}
          </AnimatePresence>

          {/* BOTÓN WHATSAPP */}
          <motion.div
            layout
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="mt-5 w-100"
            style={{ maxWidth: "500px" }}
          >
            <motion.button
              whileHover={{
                scale: 1.03,
                boxShadow: "0 6px 15px rgba(37, 211, 102, 0.4)",
              }}
              whileTap={{ scale: 0.97 }}
              onClick={() => window.open(linkWhatsapp, "_blank")}
              style={{
                width: "100%",
                padding: "0.8rem",
                borderRadius: "15px",
                border: "none",
                backgroundColor: "#25D366",
                color: "white",
                cursor: "pointer",
                fontWeight: "bold",
                fontSize: "clamp(1rem, 2.5vw, 1.2rem)",
              }}
            >
              Contáctanos por WhatsApp
            </motion.button>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
